/* ----------------------------------------------------------------------
 * LNURL-Pay – response-shape validator
 * -------------------------------------------------------------------- */

export type LnUrlOneResponse = {
  plainTextForUserDisplay: string;
  rawCallBackUrl: string;
  hTagInInvoiceMustMatch: string;
  minSendableSatoshis: number;
  maxSendableSatoshis: number;
  commentLengthAllowed: number | null;
};

/**
 * Validate that an arbitrary value matches the LnUrlOneResponse contract.
 *
 * @returns `{ ok: true, value }` when valid, otherwise
 *          `{ ok: false, error }` with a human-readable reason.
 *
 * Usage:
 * ```ts
 * const result = validateLnUrlOneResponse(json);
 * if (!result.ok) throw new Error(result.error);
 * const lnurl = result.value;  // strongly-typed
 * ```
 */
export function validateLnUrlOneResponse(
  input: unknown
): { ok: true; value: LnUrlOneResponse } | { ok: false; error: string } {
  // 0. Basic object check
  if (typeof input !== "object" || input === null)
    return { ok: false, error: "Input is not an object" };

  const obj = input as Partial<Record<keyof LnUrlOneResponse, unknown>>;

  // 1. Required string fields
  const stringFields: (keyof LnUrlOneResponse)[] = [
    "plainTextForUserDisplay",
    "rawCallBackUrl",
    "hTagInInvoiceMustMatch",
  ];
  for (const key of stringFields) {
    if (typeof obj[key] !== "string" || obj[key] === "")
      return { ok: false, error: `\`${key}\` must be a non-empty string` };
  }

  // 2. Required number fields
  const numberFields: (keyof LnUrlOneResponse)[] = [
    "minSendableSatoshis",
    "maxSendableSatoshis",
  ];
  for (const key of numberFields) {
    if (typeof obj[key] !== "number" || !Number.isFinite(obj[key]))
      return { ok: false, error: `\`${key}\` must be a finite number` };
    if ((obj[key] as number) < 0)
      return { ok: false, error: `\`${key}\` cannot be negative` };
  }

  // 3. commentLengthAllowed: number ≥0 or null
  const cla = obj.commentLengthAllowed;
  if (
    cla !== null &&
    (typeof cla !== "number" || !Number.isInteger(cla) || cla < 0)
  )
    return {
      ok: false,
      error: "`commentLengthAllowed` must be a non-negative integer or null",
    };

  // 4. min vs. max sanity
  if ((obj.minSendableSatoshis as number) > (obj.maxSendableSatoshis as number))
    return {
      ok: false,
      error: "`minSendableSatoshis` cannot exceed `maxSendableSatoshis`",
    };

  // 5. rudimentary URL sanity (skip full RFC regex for brevity)
  try {
    // throws if invalid
    new URL(obj.rawCallBackUrl as string);
  } catch {
    return { ok: false, error: "`rawCallBackUrl` is not a valid URL" };
  }

  // All checks passed → cast is safe
  return { ok: true, value: obj as LnUrlOneResponse };
}
